import { effect, inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { AccountDetailsApi } from '../models';
import { AuthService } from './auth.service';
import { EnvService } from './env.service';
import { MyInfoService } from './my-info.service';
import { SessionService } from './session.service';
import { normalizeAppLanguage } from './language-preference.utils';

@Injectable({ providedIn: 'root' })
export class LanguagePreferenceService {
  private readonly transloco = inject(TranslocoService);
  private readonly http = inject(HttpClient);
  private readonly envService = inject(EnvService);
  private readonly authService = inject(AuthService);
  private readonly sessionService = inject(SessionService);
  private readonly myInfoService = inject(MyInfoService);

  constructor() {
    effect(() => {
      const authUserId = this.sessionService.authUserId();
      const activeUserId = this.sessionService.activeUserId();
      const sessionReady = this.sessionService.sessionReady();
      const activeUserLoaded = this.myInfoService.activeUserLoaded();
      const activeUser = this.myInfoService.activeUser();

      if (
        !sessionReady
        || authUserId === null
        || activeUserId !== authUserId
        || !activeUserLoaded
        || activeUser === null
      ) {
        return;
      }

      this.applyPreference(activeUser.selectedLanguage);
    });
  }

  applyPreference(selectedLanguage: string | null | undefined): void {
    const language = normalizeAppLanguage(selectedLanguage);
    if (language === null) {
      return;
    }

    if (this.transloco.getActiveLang() !== language) {
      this.transloco.setActiveLang(language);
    }
  }

  /** Applies the authenticated user's stored preference before first paint. */
  async initializeFromServer(): Promise<void> {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    const authUserId = this.authService.authUserId();
    if (authUserId === null) {
      return;
    }

    try {
      const account = await firstValueFrom(
        this.http.get<Pick<AccountDetailsApi, 'selectedLanguage'>>(
          `${this.envService.apiUrl}/users/${authUserId}/info`,
        ),
      );
      this.applyPreference(account.selectedLanguage);
    } catch {
      // Keep Transloco default when bootstrap read fails.
    }
  }
}
